import { ToastNotifications } from '@lightspeed/design-system-react'
import { T, TFunction, useT } from '@transifex/react'
import { GiftCard, GiftCardStatus, GiftCardTransactionType } from '@vend/types'
import { fetchApi, trackError } from '@vend/utilities'
import React, { ReactNode, useCallback } from 'react'
import { getFeatureFromCache } from '../../util/feature-cache'
import { OmniGiftCardSettings } from './gift-cards-types'
import {
  GiftCardModalFlow,
  GiftCardModalState,
  GiftCardType,
} from './modal/GiftCardModal'
import {
  giftCardFromBalancesApi,
  giftCardSettingsFromBalancesApi,
} from './utils'
import { useBigCommerceGiftCerts } from './useBigCommerceGiftCerts'

// From GIFTCARD_PROVIDER_SETTINGS, BalancesGiftCardsController, BigCommerceGiftCertsController
export const useGiftCardProviderSettings = (
  flow: GiftCardModalFlow,
  giftCardType: GiftCardType
) => {
  const recordsTransactions =
    giftCardType === 'BIGCOMMERCE' ? false : giftCardType === 'BALANCES'

  let giftCardMinChars = 1
  if (
    giftCardType === 'BALANCES' &&
    flow === 'SELLING' &&
    !getFeatureFromCache('omni_gift_cards_allow_short_codes')
  ) {
    giftCardMinChars = 8
  }

  const giftCardMaxChars: number =
    (giftCardType === 'BIGCOMMERCE' && 15) ||
    (giftCardType === 'BALANCES' && 64) ||
    0

  let giftCardCapitalised: ReactNode = null
  if (giftCardType === 'BIGCOMMERCE') {
    giftCardCapitalised = (
      <T
        _str="Gift certificate"
        _context="BigCommerce term for gift card capitalised"
      />
    )
  }
  if (giftCardType === 'BALANCES') {
    giftCardCapitalised = (
      <T _str="Gift card" _context="X-series term for gift card capitalised" />
    )
  }

  let giftCardCodeCapitalised: ReactNode = null
  if (giftCardType === 'BIGCOMMERCE') {
    giftCardCodeCapitalised = (
      <T
        _str="Gift certificate code"
        _context="BigCommerce term for gift card code capitalised"
      />
    )
  }
  if (giftCardType === 'BALANCES') {
    giftCardCodeCapitalised = (
      <T
        _str="Gift card number"
        _context="X-series term for gift card code capitalised"
      />
    )
  }

  return {
    recordsTransactions,
    giftCardMinChars,
    giftCardMaxChars,
    giftCardCodeCapitalised,
    giftCardCapitalised,
  }
}

export const useGiftCardSearchStrings = (
  giftCardType: GiftCardType,
  minChars: number
) => {
  const t = useT()

  let placeholder: string | undefined
  if (giftCardType === 'BALANCES') {
    placeholder = t('Enter gift card number', {
      _context: 'Find X-series gift card placeholder',
    })
  }
  if (giftCardType === 'BIGCOMMERCE') {
    placeholder = t('Enter gift certificate code', {
      _context: 'Find BigCommerce gift card placeholder',
    })
  }

  let invalidCharactersErrorMessage: ReactNode = null
  if (giftCardType === 'BALANCES') {
    invalidCharactersErrorMessage = (
      <T
        _str="Gift card number can only contain letters, numbers or dashes"
        _context="X-series gift card code has invalid characters in input error message"
      />
    )
  }
  if (giftCardType === 'BIGCOMMERCE') {
    invalidCharactersErrorMessage = (
      <T
        _str="Gift certificate code can only contain letters, numbers or dashes"
        _context="BigCommerce gift card code has invalid characters in input error message"
      />
    )
  }

  let tooShortErrorMessage: ReactNode = null
  if (giftCardType === 'BALANCES') {
    tooShortErrorMessage = (
      <T
        _str="Gift card number must contain at least {minChars} characters"
        _context="X-series gift card code input is too short error message"
        minChars={minChars}
      />
    )
  }
  if (giftCardType === 'BIGCOMMERCE') {
    tooShortErrorMessage = (
      <T
        _str="Gift certificate code must contain at least {minChars} characters"
        _context="BigCommerce gift card code input is too short error message"
        minChars={minChars}
      />
    )
  }

  let findButtonText: ReactNode = null
  if (giftCardType === 'BALANCES') {
    findButtonText = (
      <T _str="Find gift card" _context="Find X-series gift card button" />
    )
  }
  if (giftCardType === 'BIGCOMMERCE') {
    findButtonText = (
      <T
        _str="Find gift certificate"
        _context="Find BigCommerce gift card button"
      />
    )
  }

  return {
    placeholder,
    invalidCharactersErrorMessage,
    tooShortErrorMessage,
    findButtonText,
  }
}

export const useSetValueStrings = (
  state: GiftCardModalState,
  giftCardType: GiftCardType
) => {
  let header: ReactNode = null

  if (state === 'SET_VALUE' && giftCardType === 'BALANCES') {
    header = (
      <T
        _str="Add new gift card to sale"
        _context="New X-series gift card modal header"
      />
    )
  }

  if (state === 'SET_VALUE' && giftCardType === 'BIGCOMMERCE') {
    header = (
      <T
        _str="Add new gift certificate to sale"
        _context="New BigCommerce gift card modal header"
      />
    )
  }

  if (state === 'ADD_BALANCE' && giftCardType === 'BALANCES') {
    header = (
      <T
        _str="Add funds to gift card"
        _context="Add balance to X-series gift card modal header"
      />
    )
  }

  if (state === 'ADD_BALANCE' && giftCardType === 'BIGCOMMERCE') {
    header = (
      <T
        _str="Add funds to gift certificate"
        _context="Add balance to BigCommerce gift card modal header"
      />
    )
  }

  return {
    header,
  }
}

export const useDisplayStatus = (status: GiftCardStatus) => {
  switch (status) {
    case 'ACTIVE':
      return <T _str="ACTIVE" _context="Gift card flag active" />
    case 'INACTIVE':
      return <T _str="INACTIVE" _context="Gift card flag inactive" />
    case 'EXPIRED':
      return <T _str="EXPIRED" _context="Gift card flag expired" />
    case 'REDEEMED':
      return <T _str="REDEEMED" _context="Gift card flag redeemed" />
    case 'VOIDED':
      return <T _str="CANCELED" _context="Gift card flag voided" />
    default:
      return null
  }
}

export const useViewDetailsStrings = (giftCardType: GiftCardType) => {
  let findDifferentText: ReactNode = null
  if (giftCardType === 'BALANCES') {
    findDifferentText = (
      <T
        _str="Find a different gift card"
        _context="Find different gift card button"
      />
    )
  }
  if (giftCardType === 'BIGCOMMERCE') {
    findDifferentText = (
      <T
        _str="Find a different gift certificate"
        _context="Find different gift certificate button"
      />
    )
  }

  let addFundsText: ReactNode = null
  if (giftCardType === 'BALANCES') {
    addFundsText = (
      <T
        _str="Add funds to gift card"
        _context="Add funds to X-series gift card button"
      />
    )
  }
  if (giftCardType === 'BIGCOMMERCE') {
    addFundsText = (
      <T
        _str="Add funds to gift certificate"
        _context="Add funds to BigCommerce gift card button"
      />
    )
  }

  let payFullText: ReactNode = null
  if (giftCardType === 'BALANCES') {
    payFullText = (
      <T
        _str="Pay with gift card"
        _context="Pay with X-series gift card button"
      />
    )
  }
  if (giftCardType === 'BIGCOMMERCE') {
    payFullText = (
      <T
        _str="Pay with gift certificate"
        _context="Pay with BigCommerce gift card button"
      />
    )
  }

  const payPartialText = (
    <T _str="Make partial payment" _context="Partial payment" />
  )

  let payingWithText: ReactNode = null
  if (giftCardType === 'BALANCES') {
    payingWithText = (
      <T
        _str="Paying with gift card..."
        _context="Paying with X-series gift card"
      />
    )
  } else if (giftCardType === 'BIGCOMMERCE') {
    payingWithText = (
      <T
        _str="Paying with gift certificate..."
        _context="Paying with BigCommerce gift certificate"
      />
    )
  }

  return {
    addFundsText,
    findDifferentText,
    payFullText,
    payPartialText,
    payingWithText,
  }
}

export const useGetDisplayTransactionType = () => {
  const getDisplayTransactionType = (
    transactionType: GiftCardTransactionType | 'EXPIRING'
  ) => {
    switch (transactionType) {
      case 'ACTIVATION':
        return <T _str="Issued" _context="Gift card transaction type" />
      case 'REDEEMING':
        return <T _str="Redeemed" _context="Gift card transaction type" />
      case 'IMPORTING':
        return <T _str="Imported" _context="Gift card transaction type" />
      case 'VOIDING':
        return <T _str="Cancelled" _context="Gift card transaction type" />
      case 'REVERSING':
        return <T _str="Reversed" _context="Gift card transaction type" />
      case 'EXPIRING':
        return <T _str="Expired" _context="Gift card transaction type" />
      case 'RELOADING':
        return <T _str="Reloaded" _context="Gift card transaction type" />
      default:
        null
    }
  }
  return getDisplayTransactionType
}

export const GIFTCARDS_ENDPOINT = '/api/2.0/balances/gift_cards'

const getOfflineErrorMessage = (t: TFunction, flow: string) => {
  return flow === 'SELLING'
    ? t('Gift cards can’t be sold when the sell screen is offline', {
        _context:
          'Trying to sell gift card when sell screen is offline error toast',
      })
    : t('Gift cards can’t be redeemed when the sell screen is offline', {
        _context:
          'Trying to redeem gift card when sell screen is offline error toast',
      })
}

const getDefaultErrorMessage = (t: TFunction) =>
  t('Something went wrong. Please try again.', {
    _context: 'Generic gift card error toast',
  })

export const useRetrieveRetailerGiftCardSettings = (
  flow: GiftCardModalFlow,
  onClose: () => void,
  onError: () => void
) => {
  const t = useT()

  return useCallback(async (): Promise<OmniGiftCardSettings | null> => {
    let response: Response

    try {
      response = await fetchApi(`${GIFTCARDS_ENDPOINT}/retailer/status`)
    } catch {
      ToastNotifications.negative(getOfflineErrorMessage(t, flow))
      onClose()
      return null
    }

    if (!response.ok) {
      // Server error
      if (response.status >= 500) {
        onError()
        return null
      }

      // Other errors
      ToastNotifications.negative(getDefaultErrorMessage(t))
      onClose()
      return null
    }

    const apiGiftCardSettings = await response.json()
    return giftCardSettingsFromBalancesApi(apiGiftCardSettings)
  }, [flow, onClose, onError, t])
}

export const useRetrieveGiftCard = (
  flow: GiftCardModalFlow,
  giftCardType: GiftCardType,
  giftCardSettings: OmniGiftCardSettings | null,
  onClose: () => void,
  onError: () => void
) => {
  const t = useT()
  const { retrieveBigCommerceGiftCard } = useBigCommerceGiftCerts()
  const hasOmniGiftCards = getFeatureFromCache('omni_gift_cards')

  const retrieveInStoreGiftCard = useCallback(
    async (code: string): Promise<GiftCard | null> => {
      let response: Response

      try {
        response = await fetchApi(`${GIFTCARDS_ENDPOINT}/${code}`)
      } catch {
        ToastNotifications.negative(getOfflineErrorMessage(t, flow))
        onClose()
        return null
      }

      if (response.status === 404) {
        if (flow === 'REDEEMING') {
          ToastNotifications.negative(
            t('Gift card not activated. Check again or enter another number.', {
              _context: 'Gift card not found error toast',
            })
          )
          onClose()
          return null
        }

        if (hasOmniGiftCards && !giftCardSettings) {
          ToastNotifications.negative(getDefaultErrorMessage(t))
          onClose()
          return null
        }

        // SELLING
        let expiresAt = response.headers.get('expected-expiry-date')

        const { expiry_period, expiry_period_type } = giftCardSettings || {}
        if (expiry_period && expiry_period_type) {
          // If the retailer has expiry periods set, update the expiry date
          const expiryAtPeriod = new Date()
          switch (expiry_period_type) {
            case 'DAYS':
              expiryAtPeriod.setDate(expiryAtPeriod.getDate() + expiry_period)
              break
            case 'MONTHS':
              expiryAtPeriod.setMonth(expiryAtPeriod.getMonth() + expiry_period)
              break
            case 'YEARS':
              expiryAtPeriod.setFullYear(
                expiryAtPeriod.getFullYear() + expiry_period
              )
              break
            default:
              trackError(
                `[GiftCardModal] Unknown expiry type: ${expiry_period_type}`
              )
              ToastNotifications.negative(getDefaultErrorMessage(t))
              onClose()
              return null
          }
          expiresAt = expiryAtPeriod.toISOString()
        }

        const giftCard: GiftCard = {
          // Most of these fields will be set later
          id: '',
          code,
          createdAt: '',
          expiresAt,
          initialAmount: 0,
          balance: 0,
          status: 'INACTIVE',
          transactions: [],
        }

        return giftCard
      }

      if (!response.ok) {
        if (response.status >= 500) {
          // Server error
          onError()
          return null
        }

        // Other error responses
        ToastNotifications.negative(getDefaultErrorMessage(t))
        onClose()
        return null
      }

      // Exisintg gift card found
      const giftCard = giftCardFromBalancesApi(await response.json())
      return giftCard
    },
    [t, flow, onClose, hasOmniGiftCards, giftCardSettings, onError]
  )

  return useCallback(
    (code: string) => {
      if (giftCardType === 'BIGCOMMERCE') {
        return retrieveBigCommerceGiftCard(code)
      }

      return retrieveInStoreGiftCard(code)
    },
    [giftCardType, retrieveBigCommerceGiftCard, retrieveInStoreGiftCard]
  )
}
